import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useAuth } from '../contexts/AuthContext';
import { usePacientes } from '../hooks/usePacientes';
import { getManipulatedFormulas, deleteManipulatedFormula } from '../services/manipulatedService';
import { generateManipulatedFormulaPDF } from '../utils/manipulatedPdfExport';
import { sendPDFToPatient, generatePDFBlob } from '../services/pdfSendService';
import { PDFSendModal } from '../components/pdf/PDFSendModal';
import { CreateManipulatedModal } from '../components/manipulated/CreateManipulatedModal';
import { ManipulatedHistory } from '../components/manipulated/ManipulatedHistory';
import { ManipulatedFormula } from '../services/mockData';
import './Pages.css';

export const Manipulated: React.FC = () => {
  const { user } = useAuth();
  const { pacientes } = usePacientes();
  const [formulas, setFormulas] = useState<ManipulatedFormula[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all');
  const [selectedFormula, setSelectedFormula] = useState<ManipulatedFormula | null>(null);
  const [isPDFSendModalOpen, setIsPDFSendModalOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  useEffect(() => {
    if (user) {
      loadFormulas();
    }
  }, [user, selectedPatient]);

  const loadFormulas = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const data = await getManipulatedFormulas(user.uid, selectedPatient || undefined);
      setFormulas(data);
    } catch (error) {
      console.error('Erro ao carregar fórmulas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (formulaId: string) => {
    if (!user?.uid) {
      alert('Usuário não autenticado');
      return;
    }
    try {
      await deleteManipulatedFormula(user.uid, formulaId);
      await loadFormulas();
    } catch (error) {
      console.error('Erro ao deletar fórmula:', error);
      alert('Erro ao cancelar prescrição');
    }
  };

  const handleSelectFormula = (formula: ManipulatedFormula) => {
    setSelectedFormula(formula);
  };

  const handleGeneratePDF = async (formula: ManipulatedFormula) => {
    if (!user) return;
    try {
      // Define a fórmula selecionada para o modal de envio
      setSelectedFormula(formula);
      
      const patient = pacientes.find(p => p.id === formula.patientId);
      const patientName = patient?.nome || 'Paciente';
      const nutritionistName = user.email || 'Nutricionista';

      const blob = await generateManipulatedFormulaPDF(formula, patientName, nutritionistName);
      setPdfBlob(blob);
      setIsPDFSendModalOpen(true);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF');
    }
  };

  const getPatientName = (patientId: string): string => {
    const patient = pacientes.find(p => p.id === patientId);
    return patient?.nome || 'Paciente';
  };

  const filteredFormulas = formulas.filter(f => {
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    return true;
  });

  const activeFormulas = filteredFormulas.filter(f => f.status === 'active');

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Fórmulas Manipuladas</h2>
          <p>Gerencie prescrições de fórmulas manipuladas para seus pacientes.</p>
        </div>
        <div className="ns-page-header-actions">
          <Button onClick={() => setIsModalOpen(true)}>Nova Prescrição</Button>
        </div>
      </div>

      <Card title="Filtros">
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <label className="ns-field" style={{ flex: 1, minWidth: '200px' }}>
            <span>Paciente</span>
            <select
              className="ns-input"
              value={selectedPatient}
              onChange={(e) => setSelectedPatient(e.target.value)}
            >
              <option value="">Todos os pacientes</option>
              {pacientes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="ns-field" style={{ minWidth: '150px' }}>
            <span>Status</span>
            <select
              className="ns-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              <option value="all">Todos</option>
              <option value="active">Ativas</option>
              <option value="completed">Concluídas</option>
              <option value="cancelled">Canceladas</option>
            </select>
          </label>
        </div>
      </Card>

      {activeFormulas.length > 0 && (
        <Card title="Prescrições Ativas" style={{ marginTop: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {activeFormulas.map((formula) => (
              <div
                key={formula.id}
                style={{
                  padding: '16px',
                  background: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #86efac',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                  {formula.formulaName}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginBottom: '12px' }}>
                  <div><strong>Paciente:</strong> {getPatientName(formula.patientId)}</div>
                  <div style={{ marginTop: '4px' }}><strong>Ingredientes:</strong> {formula.ingredients.length} ingredientes</div>
                  <div style={{ marginTop: '4px' }}><strong>Quantidade:</strong> {formula.quantity} {formula.unit}</div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    variant="secondary"
                    onClick={() => handleGeneratePDF(formula)}
                    style={{ fontSize: '11px', padding: '4px 8px', flex: 1 }}
                  >
                    📄 PDF
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleSelectFormula(formula)}
                    style={{ fontSize: '11px', padding: '4px 8px', flex: 1 }}
                  >
                    👁️ Ver
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <ManipulatedHistory
        formulas={filteredFormulas}
        onSelectFormula={handleSelectFormula}
        onDeleteFormula={handleDelete}
      />

      {selectedFormula && (
        <Modal
          isOpen={!!selectedFormula}
          onClose={() => setSelectedFormula(null)}
          title={`Detalhes: ${selectedFormula.formulaName}`}
          footer={
            <>
              <Button variant="secondary" onClick={() => setSelectedFormula(null)}>
                Fechar
              </Button>
              <Button onClick={() => handleGeneratePDF(selectedFormula)}>
                📄 Gerar PDF
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <strong>Paciente:</strong> {getPatientName(selectedFormula.patientId)}
            </div>
            <div>
              <strong>Ingredientes:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                {selectedFormula.ingredients.map((ing, index) => (
                  <li key={index} style={{ marginBottom: '4px' }}>
                    {ing.name} - {ing.quantity} {ing.unit}
                    {ing.purpose && <span style={{ color: 'var(--page-text-muted)' }}> ({ing.purpose})</span>}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <strong>Posologia:</strong> {selectedFormula.posology}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              <div><strong>Duração:</strong> {selectedFormula.duration} dias</div>
              <div><strong>Quantidade:</strong> {selectedFormula.quantity} {selectedFormula.unit}</div>
              <div><strong>Validade:</strong> {selectedFormula.validity} dias</div>
              <div><strong>Status:</strong> {selectedFormula.status === 'active' ? 'Ativa' : selectedFormula.status === 'completed' ? 'Concluída' : 'Cancelada'}</div>
            </div>
            {selectedFormula.laboratory && (
              <div>
                <strong>Laboratório:</strong> {selectedFormula.laboratory}
              </div>
            )}
            {selectedFormula.observations && (
              <div>
                <strong>Observações:</strong>
                <p style={{ marginTop: '4px', color: 'var(--page-text-muted)' }}>{selectedFormula.observations}</p>
              </div>
            )}
            <div style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>
              Prescrito em: {new Date(selectedFormula.prescribedAt).toLocaleDateString('pt-BR')} às {new Date(selectedFormula.prescribedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </Modal>
      )}

      <CreateManipulatedModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={loadFormulas}
        initialPatientId={selectedPatient}
      />

      {pdfBlob && selectedFormula && (
        <PDFSendModal
          isOpen={isPDFSendModalOpen}
          onClose={() => {
            setIsPDFSendModalOpen(false);
            setPdfBlob(null);
            setSelectedFormula(null);
          }}
          onSend={async (message) => {
            if (!user?.uid || !selectedFormula || !pdfBlob) return;
            try {
              const fileName = `prescricao_${selectedFormula.formulaName.replace(/\s+/g, '_')}_${getPatientName(selectedFormula.patientId).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
              await sendPDFToPatient(selectedFormula.patientId, user.uid, 'manipulated', fileName, pdfBlob, message);
              alert('PDF enviado para o paciente com sucesso!');
            } catch (error) {
              console.error('Erro ao enviar PDF:', error);
              alert('Erro ao enviar PDF. Tente novamente.');
              throw error;
            }
          }}
          pdfBlob={pdfBlob}
          fileName={`prescricao_${selectedFormula.formulaName.replace(/\s+/g, '_')}_${getPatientName(selectedFormula.patientId).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`}
          patientName={getPatientName(selectedFormula.patientId)}
        />
      )}
    </div>
  );
};

